import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TbMailOff } from "react-icons/tb";
import { Invoice } from "src/types/index";

interface Props {
    willSend: Invoice[];
    willResend: Invoice[];
    noEmail: Invoice[];
    postOnly: Invoice[];
    legalBlocked: Invoice[];
    cannotSend: Invoice[];
}

type TabKey = "send" | "resend" | "noEmail" | "postOnly" | "legal" | "skip";

function InvoiceRow({ inv }: { inv: Invoice }) {
    const { t } = useTranslation();
    const r = inv.recipient;
    const name  = r ? `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() : "—";
    const email = r?.email ?? "—";
    return (
        <>
            <strong className="bsp-grid__id">
                {inv.readableId}
                {inv.patient?.postOnly && (
                    <TbMailOff className="invoice-postonly-icon" title={t("form.postOnlyHint")} />
                )}
            </strong>
            <span className="bsp-grid__info">
                <span className="bsp-grid__info-name">{name}</span>
                <span className="bsp-grid__info-addr">{email}</span>
            </span>
            <span className="bsp-grid__cost-pages">€{inv.amount?.toFixed(2)}</span>
        </>
    );
}

export default function BulkSendPreview({ willSend, willResend, noEmail, postOnly, legalBlocked, cannotSend }: Props) {
    const { t } = useTranslation();

    const tabs: { key: TabKey; label: string; items: Invoice[] }[] = [
        willSend.length > 0     && { key: "send",    label: t("invoices.bulkSend.willSend",       { count: willSend.length }),     items: willSend },
        willResend.length > 0   && { key: "resend",  label: t("invoices.bulkSend.willResend",     { count: willResend.length }),   items: willResend },
        noEmail.length > 0      && { key: "noEmail", label: t("invoices.bulkSend.noEmail",        { count: noEmail.length }),      items: noEmail },
        postOnly.length > 0      && { key: "postOnly", label: t("invoices.bulkSend.postOnly",     { count: postOnly.length }),     items: postOnly },
        legalBlocked.length > 0 && { key: "legal",   label: t("invoices.bulkSend.legalNotAccepted", { count: legalBlocked.length }), items: legalBlocked },
        cannotSend.length > 0   && { key: "skip",    label: t("invoices.bulkSend.cannotSend",     { count: cannotSend.length }),   items: cannotSend },
    ].filter(Boolean) as { key: TabKey; label: string; items: Invoice[] }[];

    const [active, setActive] = useState<TabKey>(tabs[0]?.key ?? "send");
    const current = tabs.find((tab) => tab.key === active);

    return (
        <div className="bsp">
            <div className="bsp__tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active === tab.key}
                        className={`bsp__tab bsp__tab--${tab.key}${active === tab.key ? " bsp__tab--active" : ""}`}
                        onClick={() => setActive(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="bsp__panel" role="tabpanel">
                {current && (
                    <div className="bsp-grid bsp-grid--link">
                        <span className="bsp-grid__hdr">{t("table.id")}</span>
                        <span className="bsp-grid__hdr">{t("table.recipient")}</span>
                        <span className="bsp-grid__hdr bsp-grid__hdr--r">{t("table.amount")}</span>
                        {current.items.map((inv) => <InvoiceRow key={inv.id} inv={inv} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
